import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, X, ArrowRight } from "lucide-react";
import { searchSite } from "../data/searchIndex";

export default function SearchOverlay({ open, onClose }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const results = searchSite(query).slice(0, 8);

  useEffect(() => {
    if (open) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    if (query.trim().length >= 2) {
      onClose();
      navigate(`/recherche?q=${encodeURIComponent(query.trim())}`);
    }
  };

  return (
    <div
      data-testid="search-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Recherche dans le site"
      className="fixed inset-0 z-[90] bg-[#0B1F3A]/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mx-auto mt-16 w-[calc(100%-2rem)] max-w-xl rounded-2xl bg-white p-5 shadow-2xl sm:mt-24"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={submit} className="flex items-center gap-3">
          <Search className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            data-testid="search-overlay-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher : cote, rollover, ANJ, combiné..."
            aria-label="Rechercher dans le site"
            className="w-full border-0 bg-transparent text-base text-[#0B1F3A] outline-none placeholder:text-slate-400 focus:ring-0"
            style={{ outline: "none", boxShadow: "none" }}
          />
          <button
            type="button"
            data-testid="search-overlay-close"
            onClick={onClose}
            aria-label="Fermer la recherche"
            className="shrink-0 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </form>

        {query.trim().length >= 2 && (
          <div className="mt-4 max-h-[55vh] overflow-y-auto border-t border-slate-100 pt-3">
            {results.length === 0 && (
              <p className="px-2 py-4 text-sm text-slate-500">
                Aucun résultat pour « {query.trim()} ».
              </p>
            )}
            <ul className="space-y-1">
              {results.map((r, i) => (
                <li key={`${r.path}-${i}`}>
                  <Link
                    to={r.path}
                    data-testid={`search-result-${i}`}
                    onClick={onClose}
                    className="block rounded-lg px-3 py-2.5 transition-colors hover:bg-[#F3F5F8]"
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-[#2BB08A]">
                      {r.category}
                    </p>
                    <p className="font-display text-[15px] font-semibold text-[#0B1F3A]">
                      {r.title}
                    </p>
                    <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-slate-500">
                      {r.excerpt}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
            {results.length > 0 && (
              <button
                type="button"
                data-testid="search-see-all"
                onClick={submit}
                className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full border border-slate-200 py-2.5 text-sm font-semibold text-[#0B1F3A] transition-colors hover:border-[#2BB08A] hover:bg-[#E8F8F3]"
              >
                Voir tous les résultats
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
